import React, { useState, useEffect } from "react";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { Customer, CustomerFormData } from "./customer";
import { useAppSelector } from "../../store";
import {
  selectBranches,
  selectBranchLoading,
} from "../branch/branchSlice";
import BaseModal from "../../components/common/BaseModal";
import FormInput from "../../components/common/FormInput";

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string, data: CustomerFormData) => void;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const branches = useAppSelector(selectBranches);
  const branchesLoading = useAppSelector(selectBranchLoading);
  const selectedCustomer = useAppSelector(
    (state) => state.customers.selectedCustomer
  );
  const [formData, setFormData] = useState<CustomerFormData>({
    name: "",
    code: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    status: true,
    type: "individual",
    branchId: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (selectedCustomer) {
      setFormData({
        name: selectedCustomer.name,
        code: selectedCustomer.code,
        email: selectedCustomer.email,
        phone: selectedCustomer.phone,
        address: selectedCustomer.address,
        city: selectedCustomer.city,
        state: selectedCustomer.state,
        zipCode: selectedCustomer.zipCode,
        contactPerson: selectedCustomer.contactPerson,
        contactPhone: selectedCustomer.contactPhone,
        contactEmail: selectedCustomer.contactEmail,
        status: selectedCustomer.status,
        type: selectedCustomer.type || "individual",
        branchId: selectedCustomer.branchId,
      });
    }
  }, [selectedCustomer]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.code) newErrors.code = "Code is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.contactPerson)
      newErrors.contactPerson = "Contact person is required";
    if (!formData.contactPhone)
      newErrors.contactPhone = "Contact phone is required";
    if (!formData.contactEmail)
      newErrors.contactEmail = "Contact email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Invalid contact email format";
    }
    if (!formData.branchId) newErrors.branchId = "Branch is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && selectedCustomer) {
      onSubmit(selectedCustomer.id, formData);
      
    }
  };

  if (!selectedCustomer) {
    return null;
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Edit Customer">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* <div className="grid grid-cols-2 gap-4"> */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            icon={<IdentificationIcon className="h-5 w-5" />}
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter customer name"
            required
            error={errors.name}
          />
          <FormInput
            icon={<IdentificationIcon className="h-5 w-5" />}
            label="Code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            placeholder="Enter customer code"
            required
            error={errors.code}
          />
        </div>

        {/* <div className="grid grid-cols-2 gap-4"> */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            icon={<EnvelopeIcon className="h-5 w-5" />}
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
            required
            error={errors.email}
          />
          <FormInput
            icon={<PhoneIcon className="h-5 w-5" />}
            label="Phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter phone number"
            required
            error={errors.phone}
          />
        </div>

        <FormInput
          icon={<MapPinIcon className="h-5 w-5" />}
          label="Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Enter address"
          required
          error={errors.address}
        />

        {/* <div className="grid grid-cols-3 gap-4"> */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormInput
            icon={<MapPinIcon className="h-5 w-5" />}
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="Enter city"
            required
            error={errors.city}
          />
          <FormInput
            icon={<MapPinIcon className="h-5 w-5" />}
            label="State"
            name="state"
            value={formData.state}
            onChange={handleChange}
            placeholder="Enter state"
            required
            error={errors.state}
          />
          <FormInput
            icon={<MapPinIcon className="h-5 w-5" />}
            label="ZIP Code"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            placeholder="Enter ZIP code"
            required
            error={errors.zipCode}
          />
        </div>

        {/* <div className="grid grid-cols-3 gap-4">
          <FormInput
            icon={<UserIcon className="h-5 w-5" />}
            label="Contact Person"
            name="contactPerson"
            value={formData.contactPerson}
            onChange={handleChange}
            placeholder="Enter contact person"
            required
            error={errors.contactPerson}
          />
          <FormInput
            icon={<PhoneIcon className="h-5 w-5" />}
            label="Contact Phone"
            name="contactPhone"
            value={formData.contactPhone}
            onChange={handleChange}
            placeholder="Enter contact phone"
            required
            error={errors.contactPhone}
          />
          <FormInput
            icon={<EnvelopeIcon className="h-5 w-5" />}
            label="Contact Email"
            name="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={handleChange}
            placeholder="Enter contact email"
            required
            error={errors.contactEmail}
          />
        </div> */}

        {/* <div className="grid grid-cols-2 gap-4">
          <FormInput
            icon={<BuildingOfficeIcon className="h-5 w-5" />}
            label="Branch"
            name="branchId"
            type="select"
            value={formData.branchId}
            onChange={handleChange}
            placeholder={
              branchesLoading ? "Loading branches..." : "Select branch"
            }
            required
            error={errors.branchId}
            options={branches.map((branch) => ({
              value: branch.id,
              label: branch.name,
              disabled: !branch.status,
            }))}
            disabled={branchesLoading}
          />

          <FormInput
            label="Status"
            name="status"
            type="select"
            value={formData.status ? "active" : "inactive"}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                status: e.target.value === "active",
              }))
            }
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
        </div> */}

        {errors.submit && (
          <p className="text-sm text-red-500 dark:text-red-400 bw:text-black mt-2">
            {errors.submit}
          </p>
        )}

        <div className="flex justify-between space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center mx-4 px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-secondary-800 transition-colors"          >
          Update Customer
          </button>
        </div>
      </form>
    </BaseModal>
  );
};

export default EditCustomerModal;
